import React from "react";
import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { AppLoadingButton, LoginButton } from "../subComps/loginPieces";
import { CardLogo, CardDelivery, GiftCardCard, MerchantName } from "../subComps/getMyGift";
import { DollarTitles, SecondaryButton } from "../account/components/pieces";
import { Axios, formatMoney } from "../../common/utils";
import { baseApi } from "../../common/constants";
import type { AppContext, GiftCardReward, GetMyGiftOptions, Size } from "../../types";

interface MakeItOfficialProps {
    setStep: (v: string | number) => void;
    context: AppContext;
    card: GiftCardReward;
    getMyGiftOptions: GetMyGiftOptions | string | undefined;
    size: Size;
}

const MakeItOfficial = ({
    setStep,
    context = {} as AppContext,
    card,
    getMyGiftOptions,
    size,
}: MakeItOfficialProps): React.ReactElement | null => {
    const [loading, setLoading] = useState<boolean>(false);
    const [sp] = useState<string | null>(sessionStorage.getItem(`sp`));
    const email = context?.user?.email ?? ``;

    const sendChoice = (): Promise<void> => {
        setLoading(true);
        return Axios.put(`${baseApi}gift`, JSON.stringify({ sp, brandId: card.brandId }))
            .then(() => {
                setLoading(false);
                setStep(2);
            })
            .catch(() => {
                toast.error(`Sorry, something went wrong`);
                setLoading(false);
            });
    };

    useEffect(() => {
        if (getMyGiftOptions && getMyGiftOptions === `none`) {
            setStep(0);
        }
    }, [getMyGiftOptions]);

    return typeof card === `object` ? (
        <Box>
            <Box sx={{ textAlign: `center`, padding: `30px 0`, display: `flex`, justifyContent: `center` }}>
                <GiftCardCard>
                    <Box>
                        <CardLogo src={card.logo} />
                        <MerchantName>{card.brandName}</MerchantName>
                    </Box>
                    <Box>
                        <DollarTitles sx={{ fontSize: 20, margin: `20px 0 5px`, color: `#4a4a4a` }}>
                            {formatMoney((getMyGiftOptions as GetMyGiftOptions).amount)}
                        </DollarTitles>
                        <CardDelivery>
                            {card.delivery === `mail` ? `Physical Delivery` : `Digital Delivery`}
                        </CardDelivery>
                    </Box>
                </GiftCardCard>
            </Box>
            <Typography
                component="h1"
                variant="h4"
                sx={{
                    fontFamily: `Plus Jakarta Sans`,
                    letterSpacing: `0.01em`,
                    width: `100%`,
                    fontSize: size === `small` ? `28px` : `36px`,
                    fontWeight: 800,
                    textAlign: `center`,
                }}
            >
                Ready to make it official?
            </Typography>
            <Box
                sx={{
                    display: `flex`,
                    flexDirection: `column`,
                    width: `100%`,
                    gap: 1,
                    marginTop: 1,
                    marginBottom: 3,
                }}
            >
                <Typography
                    sx={{
                        mt: `6px`,
                        fontSize: 18,
                        color: `#4a4a4a`,
                        textWrap: `balance`,
                        textAlign: `center`,
                        minWidth: `2px`,
                        fontWeight: 500,
                    }}
                >
                    Make sure this is the one you want. Once your gift is sent it can&apos;t be swapped out.{" "}
                    {card.delivery === `embedded` && (
                        <span>
                            You&apos;ll receive your gift at{` `}
                            <Typography
                                sx={{ fontWeight: 700, display: `inline`, fontSize: 18, verticalAlign: `bottom` }}
                            >
                                {email}
                            </Typography>
                            .
                        </span>
                    )}
                    {card.delivery === `mail` && <span>Your gift will be mailed to you.</span>}
                </Typography>
                <Box sx={{ textAlign: `center`, mt: 5, display: `flex`, justifyContent: `center` }}>
                    {!loading && (
                        <LoginButton
                            sx={{
                                background: `linear-gradient(1deg, #007238, #00a651)`,
                                borderRadius: `50px`,
                                margin: `0 10px 0 0`,
                            }}
                            onClick={sendChoice}
                            variant="contained"
                        >
                            Send My Gift
                        </LoginButton>
                    )}
                    {loading && (
                        <AppLoadingButton
                            sx={{
                                background: `linear-gradient(1deg, #007238, #00a651)`,
                                borderRadius: `50px`,
                                margin: `0 10px 0 0`,
                            }}
                            spinnerColor="#FFF"
                            loading
                            variant="contained"
                        >
                            Send My Gift
                        </AppLoadingButton>
                    )}
                    <LoginButton
                        sx={{
                            background: `linear-gradient(1deg, #007238, #00a651)`,
                            borderRadius: `50px`,
                            margin: `0 0 0 10px`,

                            padding: `3px !important`,
                        }}
                        onClick={() => setStep(0)}
                        variant="contained"
                    >
                        <SecondaryButton
                            sx={{
                                color: `#007238`,
                                background: `#FFF`,
                                borderRadius: `50px`,
                                height: 34,
                                "&:hover": {
                                    background: `#FFF`,
                                },
                            }}
                        >
                            Pick Another Gift
                        </SecondaryButton>
                    </LoginButton>
                </Box>
            </Box>
        </Box>
    ) : null;
};

export default MakeItOfficial;
